import React from 'react';
import { Modal, Table, Button, Space, Typography, Tag, Descriptions, message } from 'antd';
import { CopyOutlined, CloseOutlined, EnvironmentOutlined } from '@ant-design/icons';
import theme from '../theme';

const { Text } = Typography;

function PoleListModal({
  showPoleList,
  polePositions,
  poleDistance,
  totalFenceLength,
  gamefenceClosed,
  gamefenceArea,
  copyFeedback,
  onClose,
  onCopyData
}) {
  const columns = [
    {
      title: 'Pole #',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      align: 'center',
    },
    {
      title: 'Type',
      key: 'type',
      width: 150,
      render: (_, pole) => (
        pole.isMarker ? (
          <Tag color={theme.colors.success} icon={<EnvironmentOutlined />}>
            Marker {pole.markerIndex + 1}
          </Tag>
        ) : (
          <Tag color={theme.colors.mediumGrey}>Interval</Tag>
        )
      ),
    },
    {
      title: 'Latitude',
      key: 'latitude',
      render: (_, pole) => pole.position.lat.toFixed(6),
    },
    {
      title: 'Longitude',
      key: 'longitude',
      render: (_, pole) => pole.position.lng.toFixed(6),
    },
    {
      title: 'Distance from Start (m)',
      key: 'distanceFromStart',
      render: (_, pole) => pole.distanceFromStart.toFixed(2),
      align: 'right',
    },
    {
      title: 'Distance from Previous (m)',
      key: 'distanceFromPrevious',
      render: (_, pole) => pole.distanceFromPrevious.toFixed(2),
      align: 'right',
    },
  ];

  const handleCopy = async () => {
    await onCopyData();
    message.success('Pole positions copied to clipboard!');
  };

  return (
    <Modal
      title={
        <Space>
          <EnvironmentOutlined style={{ color: theme.colors.primary }} />
          <span>Pole Positions ({polePositions.length} poles)</span>
        </Space>
      }
      open={showPoleList}
      onCancel={onClose}
      width="90%"
      style={{ maxWidth: '1200px' }}
      footer={[
        <Button
          key="copy"
          type="primary"
          icon={<CopyOutlined />}
          onClick={handleCopy}
          style={{ backgroundColor: theme.colors.primary, borderColor: theme.colors.primary }}
        >
          Copy to Clipboard
        </Button>,
        <Button key="close" onClick={onClose} icon={<CloseOutlined />}>
          Close
        </Button>,
      ]}
    >
      <Descriptions
        bordered
        column={2}
        size="small"
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label="Pole Distance">
          <Text strong>{poleDistance} m</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Total Length">
          <Text strong>{totalFenceLength.toFixed(2)} m</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Fence Type">
          <Tag color={gamefenceClosed ? theme.colors.success : theme.colors.info}>
            {gamefenceClosed ? 'Closed' : 'Open'}
          </Tag>
        </Descriptions.Item>
        {gamefenceClosed && gamefenceArea > 0 && (
          <Descriptions.Item label="Enclosed Area">
            <Text strong>
              {gamefenceArea.toLocaleString()} m² ({(gamefenceArea / 10000).toFixed(2)} hectares)
            </Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      <Table
        columns={columns}
        dataSource={polePositions}
        rowKey="index"
        pagination={{ pageSize: 10, showSizeChanger: true }}
        size="small"
        rowClassName={(record) => record.isMarker ? 'pole-marker-row' : ''}
        scroll={{ x: 800 }}
      />

      <style jsx>{`
        .pole-marker-row {
          background-color: ${theme.colors.primaryPale};
        }
      `}</style>
    </Modal>
  );
}

export default PoleListModal;